import {
  ButtonField,
  ButtonGroup,
  ButtonGroupItem,
  ButtonFieldIcon,
  CheckboxField,
  InputField,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@wk/components-react16';

import {
  IconArrowLeft,
  IconCircleCheck,
  IconClock,
  IconFileText,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';

import { SimpleEditor } from '@/editor/tiptap/components/tiptap-templates/simple/simple-editor';

import { getReleaseNoteAuthorName } from '@/features/newsletter/lib/author';
import { useNewsletterAdminController } from './useNewsletterAdminController';

import './NewsletterAdminPage.css';

const NewsletterAdmin = () => {
  const {
    deleteModalProps,
    formProps,
    headerProps,
    savedNewslettersProps,
  } = useNewsletterAdminController();

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ButtonFieldIcon>
            <button type="button" onClick={headerProps.onBack}>
              <IconArrowLeft size={22} />
            </button>
          </ButtonFieldIcon>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <IconFileText size={24} />
            Newsletter Admin
          </h1>
        </div>
        <div className="flex gap-2">
          <ButtonField mode="text">
            <button type="button" onClick={headerProps.onViewPublished}>
              View published newsletters
            </button>
          </ButtonField>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 flex-1 min-h-0 overflow-hidden">
        <form
          onSubmit={formProps.onSubmit}
          className="xl:col-span-3 flex flex-col gap-3 border rounded-lg bg-white p-4 shadow-sm overflow-auto min-h-0"
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{formProps.formTitle}</h2>
              <div className="flex items-center gap-3">
                {formProps.editingLabel ? (
                  <span className="text-xs text-gray-500">
                    {formProps.editingLabel}
                  </span>
                ) : null}
              </div>
            </div>
            <InputField label="Title">
              <input
                name="title"
                value={formProps.formState.title}
                onChange={formProps.onTitleChange}
                placeholder="Newsletter title"
              />
            </InputField>
            <InputField label="Display start">
              <input
                type="datetime-local"
                name="startAt"
                value={formProps.formState.startAt}
                onChange={formProps.onStartAtChange}
              />
            </InputField>
            <CheckboxField label="Published">
              <input
                type="checkbox"
                name="published"
                checked={formProps.formState.published}
                onChange={formProps.onPublishedChange}
              />
            </CheckboxField>
          </div>

          <div className="flex flex-col gap-2 min-h-[320px]">
            <p className="text-sm font-semibold">Content</p>
            <div className="border rounded-md min-h-[280px] max-h-[600px] overflow-auto newsletter-editor">
              <SimpleEditor
                key={formProps.editorKey}
                value={formProps.formState.content}
                onChange={formProps.onContentChange}
                placeholder="Write the newsletter content..."
                enableTables
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <ButtonField mode="text">
              <button
                type="button"
                onClick={formProps.onReset}
                disabled={formProps.isSaving}
              >
                Reset
              </button>
            </ButtonField>
            <ButtonField>
              <button type="submit" disabled={formProps.isSaving}>
                {formProps.formState.id ? 'Update' : 'Save'}
              </button>
            </ButtonField>
          </div>
        </form>

        <div className="xl:col-span-2 flex flex-col gap-3 h-full min-h-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Saved newsletters</h2>
            <div className="text-xs text-gray-500">
              {savedNewslettersProps.totalCount} total
            </div>
          </div>
          <div className="flex flex-col gap-3 overflow-auto pr-1 min-h-0">
            {savedNewslettersProps.isLoading ? (
              <div className="border rounded-lg p-4 bg-white shadow-sm text-sm text-gray-600">
                Loading newsletters...
              </div>
            ) : savedNewslettersProps.items.length === 0 ? (
              <div className="border rounded-lg p-4 bg-white shadow-sm text-sm text-gray-600">
                No newsletters stored yet. Create one to publish it.
              </div>
            ) : (
              savedNewslettersProps.items.map((item) => (
                <div
                  key={item.page.id}
                  className="border rounded-lg p-4 bg-white shadow-sm flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded-full ${item.status.color}`}
                        >
                          {item.status.label}
                        </span>
                        <span className="text-xs text-gray-500">
                          Starts {item.startAtLabel}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold">
                        {item.page.title || 'Untitled'}
                      </h3>
                      <div className="text-xs text-gray-500">
                        Author {getReleaseNoteAuthorName(item.page)}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <IconClock size={14} />
                        Updated {item.updatedAtLabel}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <ButtonField mode="ghost">
                        <button
                          type="button"
                          onClick={() =>
                            savedNewslettersProps.onEdit(item.page)
                          }
                          disabled={savedNewslettersProps.isSaving}
                        >
                          <IconPencil size={16} />
                          Edit
                        </button>
                      </ButtonField>
                      <ButtonField mode="text">
                        <button
                          type="button"
                          className="text-red-600"
                          onClick={() =>
                            savedNewslettersProps.onDelete(item.page)
                          }
                          disabled={savedNewslettersProps.isDeletingNewsletter}
                        >
                          <IconTrash size={16} />
                          Delete
                        </button>
                      </ButtonField>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 flex items-center gap-1">
                    {item.page.published ? (
                      <IconCircleCheck
                        size={16}
                        className="text-emerald-600"
                      />
                    ) : (
                      <IconClock size={16} className="text-amber-600" />
                    )}
                    {item.page.published ? 'Published' : 'Not published'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={deleteModalProps.isOpen}
        srLabelledBy="delete-newsletter-modal-title"
        srDescribedBy="delete-newsletter-modal-description"
        onUserRequest={deleteModalProps.onClose}
      >
        <ModalHeader
          titleId="delete-newsletter-modal-title"
          onUserRequest={deleteModalProps.onClose}
        >
          Delete newsletter
        </ModalHeader>
        <ModalBody descriptionId="delete-newsletter-modal-description">
          <p>
            Delete "{deleteModalProps.newsletterTitle}"? This cannot be
            undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button
                  type="button"
                  onClick={deleteModalProps.onClose}
                  disabled={deleteModalProps.isDeletingNewsletter}
                >
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button
                  type="button"
                  className="text-red-600"
                  onClick={deleteModalProps.onConfirm}
                  disabled={deleteModalProps.isDeletingNewsletter}
                >
                  Delete
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default NewsletterAdmin;
